/**
 * @zendir/ui - ZenSpace3D Cesium Backend
 *
 * Cesium-based 3D Earth/orbit view. Entities are reconciled **in-place** on
 * each data update (position changes only) to avoid the WebGL context churn
 * that occurs when entities are removed and re-added every polling tick.
 *
 * Layer visibility is controlled via the `layers` prop
 * (all default to "sensible" if the key is absent):
 *
 *   layers.labels   — spacecraft / GS name labels          (default: false)
 *   layers.coverage — coverage footprint ellipses + cones  (default: false)
 *   layers.orbits   — orbit-path polylines                 (default: true)
 *
 * Coverage geometry sources (checked in order per satellite):
 *   1. Explicit `satelliteCoverages[].footprintPolygon` — ground polygon from SDK
 *   2. Explicit `satelliteCoverages[].halfAngle`        — circular cone from half-angle
 *   3. Auto-computed circular ellipse from altitude     (when layers.coverage = true)
 *
 * Additional explicit coverage options per `SatelliteCoverage`:
 *   showCone      — 3-D sensor cone from satellite to footprint
 *   showNadirLine — dashed line from satellite to sub-satellite point
 */

import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import type {
  ZenSpace3DProps,
  ZenSpace3DHandle,
  ViewMode,
  Vector3D,
  SatelliteCoverage,
} from "./ZenSpace3DTypes";

// ── Constants ─────────────────────────────────────────────────────────────────

const EARTH_RADIUS_KM = 6371;

// ── Coordinate helpers ────────────────────────────────────────────────────────

/**
 * Convert an ECI / J2000 position (km, Earth-centred) into a Cesium
 * `Cartesian3`. Cesium's Cartesian3 lives in the **ECEF (Earth-Fixed)**
 * frame — the same frame Cesium for Unreal exposes when its
 * `CesiumGeoreference` origin is set to `TrueOrigin (0,0,0)`. We rotate
 * ECI → ECEF using `Transforms.computeTemeToPseudoFixedMatrix(jd)`,
 * which is GMST-based and accurate to sub-arcsec for visualisation
 * (no IERS data required, fully synchronous).
 *
 * Returns `null` for non-finite inputs so callers can skip stale ticks
 * without crashing Cesium's Cartesian primitives.
 *
 * @param positionKm  ECI position in km (Earth-centred).
 * @param julianDate  Cesium JulianDate at which to evaluate Earth's rotation.
 *                    Caller is responsible for advancing this with sim time.
 */
function eciKmToFixedCartesian3(
  Cesium: any,
  positionKm: Vector3D,
  julianDate: any,
): any | null {
  if (
    !positionKm ||
    !Number.isFinite(positionKm.x) ||
    !Number.isFinite(positionKm.y) ||
    !Number.isFinite(positionKm.z)
  ) {
    return null;
  }
  // ECI position in metres — Cesium works in metres throughout.
  const eciM = new Cesium.Cartesian3(
    positionKm.x * 1000,
    positionKm.y * 1000,
    positionKm.z * 1000,
  );
  const rot = Cesium.Transforms.computeTemeToPseudoFixedMatrix(julianDate);
  if (!rot) {
    // Should never happen, but Transforms can return undefined for invalid
    // dates — fall back to treating the input as already-fixed so we never
    // crash the viewer on a stray NaN date.
    return eciM;
  }
  return Cesium.Matrix3.multiplyByVector(rot, eciM, new Cesium.Cartesian3());
}

/**
 * Sub-satellite (lat/lon) point + altitude (km) of an ECI position at a
 * given epoch. Used by coverage/cone code that needs to draw on Earth's
 * surface beneath the satellite.
 */
function eciKmToSubSatellite(
  Cesium: any,
  positionKm: Vector3D,
  julianDate: any,
): { latitude: number; longitude: number; altitude: number } | null {
  const fixed = eciKmToFixedCartesian3(Cesium, positionKm, julianDate);
  if (!fixed) return null;
  const carto = Cesium.Cartographic.fromCartesian(fixed);
  if (!carto || !Number.isFinite(carto.height)) return null;
  return {
    latitude: Cesium.Math.toDegrees(carto.latitude),
    longitude: Cesium.Math.toDegrees(carto.longitude),
    altitude: carto.height / 1000, // m → km
  };
}

// ── Coverage geometry ─────────────────────────────────────────────────────────

/**
 * Circular coverage footprint radius (m) on Earth surface.
 *
 * Spherical geometry:
 *   α = arcsin(R·cos(ε) / (R+h))   — nadir angle
 *   λ = π/2 − ε − α                — Earth central angle
 *   radius = R · λ                 — arc length on surface
 */
function autoFootprintRadiusM(altitudeKm: number, minElevDeg: number): number {
  if (altitudeKm <= 0) return 0;
  const R = EARTH_RADIUS_KM;
  const eps = (minElevDeg * Math.PI) / 180;
  const sinAlpha = (R * Math.cos(eps)) / (R + altitudeKm);
  if (sinAlpha >= 1) return 0;
  const alpha = Math.asin(sinAlpha);
  const lambda = Math.PI / 2 - eps - alpha;
  return R * 1000 * lambda;
}

/** Radius (m) of the footprint circle for a sensor with the given half-angle. */
function halfAngleFootprintRadiusM(
  altitudeKm: number,
  halfAngleDeg: number,
): number {
  return altitudeKm * 1000 * Math.tan((halfAngleDeg * Math.PI) / 180);
}

// ── Entity helpers ────────────────────────────────────────────────────────────

type EntityMap = { current: Map<string, any> };

function removeFromMap(viewer: any, id: string, map: EntityMap): void {
  if (map.current.has(id)) {
    try {
      viewer.entities.removeById(id);
    } catch (_) {
      /* already gone */
    }
    map.current.delete(id);
  }
}

function makePointGraphics(Cesium: any, color: any, pixelSize = 8) {
  return {
    pixelSize,
    color,
    outlineColor: Cesium.Color.WHITE,
    outlineWidth: 1,
    scaleByDistance: new Cesium.NearFarScalar(1e6, 1.5, 5e7, 0.5),
    // Always render on top so satellites hidden behind Earth are still visible
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
  };
}

function makeLabelOpts(Cesium: any, text: string, fillColor?: any) {
  return {
    text,
    font: "12px monospace",
    fillColor: fillColor ?? Cesium.Color.WHITE,
    outlineColor: Cesium.Color.BLACK,
    outlineWidth: 2,
    style: Cesium.LabelStyle.FILL_AND_OUTLINE,
    pixelOffset: new Cesium.Cartesian2(12, 0),
    scaleByDistance: new Cesium.NearFarScalar(1e6, 1.0, 5e7, 0.3),
    // Fade out label when camera is very far away
    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 3e7),
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
  };
}

/** Add or update a circular ellipse entity on the Earth surface. */
function upsertEllipse(
  viewer: any,
  Cesium: any,
  id: string,
  position: any,
  radiusM: number,
  color: any,
  map: EntityMap,
): void {
  const existing = map.current.get(id);
  if (existing) {
    existing.position = new Cesium.ConstantPositionProperty(position);
    existing.ellipse.semiMajorAxis = new Cesium.ConstantProperty(radiusM);
    existing.ellipse.semiMinorAxis = new Cesium.ConstantProperty(radiusM);
  } else {
    const entity = viewer.entities.add({
      id,
      position,
      ellipse: {
        semiMajorAxis: radiusM,
        semiMinorAxis: radiusM,
        material: color.withAlpha(0.08),
        outline: true,
        outlineColor: color.withAlpha(0.5),
        outlineWidth: 1,
        height: 0,
      },
    });
    map.current.set(id, entity);
  }
}

/**
 * Render (or update in-place) the full coverage geometry for one satellite
 * from an explicit `SatelliteCoverage` record supplied by the Zendir SDK.
 */
function renderExplicitCoverage(
  viewer: any,
  Cesium: any,
  satId: string,
  satPos3: Vector3D,
  cov: SatelliteCoverage,
  julianDate: any,
  covMap: EntityMap,
  coneMap: EntityMap,
  nadirMap: EntityMap,
): void {
  const baseColor = cov.color
    ? Cesium.Color.fromCssColorString(cov.color)
    : Cesium.Color.CYAN;
  const opacity = cov.opacity ?? 0.2;

  const resolvedPos = cov.satellitePosition ?? satPos3;
  const satCesPos = eciKmToFixedCartesian3(Cesium, resolvedPos, julianDate);
  const subSat = eciKmToSubSatellite(Cesium, resolvedPos, julianDate);
  const latitude = subSat?.latitude ?? NaN;
  const longitude = subSat?.longitude ?? NaN;
  const altitude = subSat?.altitude ?? NaN;

  const nadirLat = cov.nadirPoint?.latitude ?? latitude;
  const nadirLon = cov.nadirPoint?.longitude ?? longitude;

  // ── Footprint polygon or circular ellipse ──────────────────────────────────
  const covId = `cov-${satId}`;
  if (
    cov.showFootprint !== false &&
    cov.footprintPolygon &&
    cov.footprintPolygon.length >= 3
  ) {
    const positions = Cesium.Cartesian3.fromDegreesArray(
      cov.footprintPolygon.flatMap((pt) => [pt.longitude, pt.latitude]),
    );
    const existing = covMap.current.get(covId);
    if (existing) {
      existing.polygon.hierarchy = new Cesium.ConstantProperty(
        new Cesium.PolygonHierarchy(positions),
      );
    } else {
      const entity = viewer.entities.add({
        id: covId,
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(positions),
          material: baseColor.withAlpha(opacity),
          outline: true,
          outlineColor: baseColor.withAlpha(opacity * 3),
          height: 0,
          clampToGround: true,
        },
      });
      covMap.current.set(covId, entity);
    }
  } else if (
    cov.showFootprint !== false &&
    cov.halfAngle != null &&
    Number.isFinite(altitude) &&
    altitude > 0
  ) {
    const subSatPos = Cesium.Cartesian3.fromDegrees(nadirLon, nadirLat, 0);
    const radius = halfAngleFootprintRadiusM(altitude, cov.halfAngle);
    upsertEllipse(viewer, Cesium, covId, subSatPos, radius, baseColor, covMap);
  } else {
    removeFromMap(viewer, covId, covMap);
  }

  // ── Sensor cone (satellite → footprint, rendered as a Cesium cylinder) ─────
  const coneId = `cone-${satId}`;
  if (cov.showCone && satCesPos && Number.isFinite(altitude) && altitude > 0) {
    const radius =
      cov.halfAngle != null
        ? halfAngleFootprintRadiusM(altitude, cov.halfAngle)
        : autoFootprintRadiusM(altitude, 5);
    const coneLength = altitude * 1000; // km → m
    // Cylinder centered at mid-altitude between satellite and ground point
    const midPos = Cesium.Cartesian3.fromDegrees(
      nadirLon,
      nadirLat,
      coneLength / 2,
    );
    const existing = coneMap.current.get(coneId);
    if (existing) {
      existing.position = new Cesium.ConstantPositionProperty(midPos);
      existing.cylinder.length = new Cesium.ConstantProperty(coneLength);
      existing.cylinder.bottomRadius = new Cesium.ConstantProperty(radius);
    } else {
      const entity = viewer.entities.add({
        id: coneId,
        position: midPos,
        cylinder: {
          length: coneLength,
          topRadius: 0,
          bottomRadius: radius,
          material: baseColor.withAlpha(0.06),
          outline: true,
          outlineColor: baseColor.withAlpha(0.25),
          numberOfVerticalLines: 12,
        },
      });
      coneMap.current.set(coneId, entity);
    }
  } else {
    removeFromMap(viewer, coneId, coneMap);
  }

  // ── Nadir line (satellite → sub-satellite point) ───────────────────────────
  const nadirId = `nadir-${satId}`;
  if (cov.showNadirLine && satCesPos) {
    const groundPos = Cesium.Cartesian3.fromDegrees(nadirLon, nadirLat, 0);
    const existing = nadirMap.current.get(nadirId);
    if (existing) {
      existing.polyline.positions = new Cesium.ConstantProperty([
        satCesPos,
        groundPos,
      ]);
    } else {
      const entity = viewer.entities.add({
        id: nadirId,
        polyline: {
          positions: [satCesPos, groundPos],
          width: 1,
          material: new Cesium.PolylineDashMaterialProperty({
            color: baseColor.withAlpha(0.5),
            dashLength: 8,
          }),
        },
      });
      nadirMap.current.set(nadirId, entity);
    }
  } else {
    removeFromMap(viewer, nadirId, nadirMap);
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface ZenSpace3DCesiumProps extends ZenSpace3DProps {
  /** Cesium module from dynamic import('cesium') */
  cesium: any;
}

export const ZenSpace3DCesium = forwardRef<
  ZenSpace3DHandle,
  ZenSpace3DCesiumProps
>(function ZenSpace3DCesium(
  {
    satellites = [],
    stations = [],
    groundStations = [],
    customObjects = [],
    orbitPaths = [],
    satelliteCoverages = [],
    view = "earth-orbit",
    height = 600,
    callbacks,
    showTools = false,
    layers,
    visibility,
    referenceTime,
    cesium: Cesium,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);

  // Stash callbacks in a ref so click/hover handlers never close over a stale
  // function reference — and never make the mount effect depend on them.
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // ── Entity maps for in-place reconciliation ────────────────────────────────
  // Each map: entity-id → Cesium entity object (only live entities live here).
  const satMapRef = useRef<Map<string, any>>(new Map()); // spacecraft + station dots
  const gsMapRef = useRef<Map<string, any>>(new Map()); // ground station dots
  const covMapRef = useRef<Map<string, any>>(new Map()); // coverage ellipses / polygons
  const coneMapRef = useRef<Map<string, any>>(new Map()); // sensor cones
  const nadirMapRef = useRef<Map<string, any>>(new Map()); // nadir lines
  const orbitMapRef = useRef<Map<string, any>>(new Map()); // orbit polylines

  // ── Latest ECI positions for satellite callback-properties ─────────────────
  // Satellites use CallbackPositionProperty so Cesium re-derives ECEF from the
  // stored ECI position on every rendered frame (using the current JulianDate).
  // This makes satellites track Earth's rotation smoothly between API poll ticks,
  // fixing the "satellite frozen while Earth rotates" bug.
  const eciPositionsRef = useRef<Map<string, Vector3D>>(new Map());

  // ── Layer visibility ──────────────────────────────────────────────────────
  // Render-flag layers (always-on by default for orbits; off by default for
  // labels/coverage). Derive primitives so the sync effect deps are cheap.
  const showLabels = layers?.labels === true; // off by default
  const showCoverage = layers?.coverage === true; // off by default
  const showOrbits = layers?.orbits !== false; // on by default

  // Object-group visibility — toggle whole categories of markers off without
  // unmounting them. Defaults to "everything visible" so existing consumers
  // who never set `visibility` are unaffected.
  const showSpacecraft = visibility?.spacecraft !== false;
  const showGroundStations = visibility?.groundStations !== false;
  const showCelestialBodies = visibility?.celestialBodies !== false;

  // ── Imperative handle ──────────────────────────────────────────────────────
  const focusOn = useCallback(
    (objectId: string, options?: { zoom?: number; animate?: boolean }) => {
      const viewer = viewerRef.current;
      if (!viewer) return;
      const entity = viewer.entities.getById(objectId);
      if (entity) {
        viewer.zoomTo(entity, {
          duration: options?.animate !== false ? 1.5 : 0,
        });
      }
    },
    [],
  );

  const setView = useCallback(
    (mode: ViewMode) => {
      const viewer = viewerRef.current;
      if (!viewer) return;
      if (mode === "earth-orbit") {
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(0, 0, 2e7),
        });
      } else if (mode === "solar-system") {
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(0, 0, 5e7),
        });
      }
    },
    [Cesium],
  );

  useImperativeHandle(
    ref,
    (): ZenSpace3DHandle => ({
      focusOn,
      flyTo: focusOn,
      setView,
      setTime: () => {},
      setTimeScale: () => {},
      togglePlay: () => {},
      setLayerVisibility: () => {},
      getCameraState: () => ({}) as any,
      setCameraState: () => {},
      exportImage: () => "",
      exportScene: () => new Blob(),
      getVisibleObjects: () => [],
      findObjects: () => [],
      addObject: () => {},
      removeObject: () => {},
      updateObject: () => {},
      measureDistance: () => 0,
    }),
    [focusOn, setView],
  );

  // ── Mount effect: create Cesium Viewer + event handlers ONCE ──────────────
  //
  // Does NOT depend on the data arrays (satellites, orbitPaths, etc.) or on
  // `callbacks`. Both produce fresh references on every parent render / poll
  // tick. Including them would tear down + rebuild Cesium every 2 s, leaking
  // WebGL contexts until Chrome hits its hard cap (~16).
  useEffect(() => {
    if (!containerRef.current || !Cesium) return;

    // Clear maps in case this is a hot-reload remount
    satMapRef.current.clear();
    gsMapRef.current.clear();
    covMapRef.current.clear();
    coneMapRef.current.clear();
    nadirMapRef.current.clear();
    orbitMapRef.current.clear();
    eciPositionsRef.current.clear();

    const viewer = new Cesium.Viewer(containerRef.current, {
      baseLayerPicker: false,
      fullscreenButton: false,
      vrButton: false,
      geocoder: false,
      homeButton: !!showTools,
      sceneModePicker: false,
      timeline: false,
      navigationHelpButton: !!showTools,
      animation: false,
      scene3DOnly: true,
      useDefaultRenderLoop: true,
      requestRenderMode: false,
    });

    viewer.scene.globe.enableLighting = true;
    viewer.scene.backgroundColor = Cesium.Color.BLACK;

    // Surface the actual exception text on render-loop crashes. Without
    // this listener Cesium only shows "An error occurred while rendering."
    // and the stack trace is buried.
    viewer.scene.renderError.addEventListener((scene: any, err: unknown) => {
      // eslint-disable-next-line no-console
      console.error("[ZenSpace3D] Cesium render error:", err);
    });

    // Advance the clock at 1× real time so Cesium continuously evaluates
    // ECI→ECEF for the correct current moment. Without this, the JulianDate
    // passed to satellite CallbackPositionProperty callbacks would be frozen
    // at the viewer-creation instant and satellites would drift relative to
    // Earth as wall-clock time advanced.
    viewer.clock.currentTime = Cesium.JulianDate.now();
    viewer.clock.multiplier = 1;
    viewer.clock.shouldAnimate = true;

    viewerRef.current = viewer;

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    // Click → select
    handler.setInputAction((movement: any) => {
      const picked = viewer.scene.pick(movement.position);
      if (
        Cesium.defined(picked) &&
        picked.id &&
        callbacksRef.current?.onSelect
      ) {
        callbacksRef.current.onSelect({
          id: picked.id.id,
          name: picked.id.name,
          category: "satellite",
          position: { x: 0, y: 0, z: 0 },
          visualRadius: 0,
        });
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Hover → onHover callback
    handler.setInputAction((movement: any) => {
      const picked = viewer.scene.pick(movement.endPosition);
      if (Cesium.defined(picked) && picked.id) {
        callbacksRef.current?.onHover?.({
          id: picked.id.id,
          name: picked.id.name,
          category: "satellite",
          position: { x: 0, y: 0, z: 0 },
          visualRadius: 0,
        });
      } else {
        callbacksRef.current?.onHover?.(null);
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    return () => {
      handler.destroy();
      satMapRef.current.clear();
      gsMapRef.current.clear();
      covMapRef.current.clear();
      coneMapRef.current.clear();
      nadirMapRef.current.clear();
      orbitMapRef.current.clear();
      eciPositionsRef.current.clear();
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [Cesium, showTools]);

  // ── Sync effect: reconcile entities on data / layer changes ───────────────
  //
  // For existing satellites (same ID, new position): entity.position is
  // updated in-place via ConstantPositionProperty — no remove/add required.
  // Only genuinely new or removed satellites trigger entity creation/deletion.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !Cesium) return;

    // JulianDate used for static ECI→ECEF snapshots in coverage/orbit
    // geometry. Satellite dot positions use CallbackPositionProperty and
    // re-evaluate this conversion per-frame from the viewer's live clock,
    // so they don't depend on `jd` here.
    const jd = referenceTime
      ? Cesium.JulianDate.fromDate(referenceTime)
      : Cesium.JulianDate.now();

    // ── Satellites + stations ────────────────────────────────────────────
    // When `visibility.spacecraft === false` the consumer wants the whole
    // group hidden — feed the loop an empty list so the cleanup pass below
    // sweeps existing entities without us having to duplicate the removal
    // logic here.
    const seenSats = new Set<string>();
    const renderedSats = showSpacecraft ? [...satellites, ...stations] : [];

    renderedSats.forEach((obj) => {
      const id = obj.id;
      const pos3 = (obj as any).position as Vector3D;
      if (!pos3) return;
      // Guard: reject placeholder/zero positions that haven't been seeded yet.
      if (
        !Number.isFinite(pos3.x) ||
        !Number.isFinite(pos3.y) ||
        !Number.isFinite(pos3.z)
      )
        return;

      // Store the latest ECI km position so the per-frame callback can
      // convert it to ECEF at the actual render time rather than at the
      // (stale) poll-tick time.
      eciPositionsRef.current.set(id, pos3);
      seenSats.add(id);

      const name = (obj as any).name ?? id;
      const existing = satMapRef.current.get(id);

      if (existing) {
        // ECI position already written to eciPositionsRef above — the
        // CallbackPositionProperty will pick it up on the next frame.
        // Only sync label visibility here.
        if (existing.label) {
          existing.label.show = new Cesium.ConstantProperty(showLabels);
        } else if (showLabels) {
          existing.label = new Cesium.LabelGraphics(
            makeLabelOpts(Cesium, name),
          );
        }
      } else {
        // Use CallbackPositionProperty so Cesium re-evaluates the ECI→ECEF
        // transform on every rendered frame using the viewer's current
        // JulianDate. This keeps the satellite locked to the correct point
        // on the rotating Earth rather than drifting in inertial space.
        const capturedId = id; // capture for closure
        // The callback runs inside Cesium's render loop on every frame.
        // ANY exception here halts rendering ("An error occurred while
        // rendering. Rendering has stopped."), so we defensively catch
        // and return undefined — Cesium treats that as "position
        // unavailable" and skips the entity for that frame.
        const posProperty = new Cesium.CallbackPositionProperty(
          (time: any) => {
            try {
              const eciKm = eciPositionsRef.current.get(capturedId);
              if (!eciKm) return undefined;
              const fixed = eciKmToFixedCartesian3(Cesium, eciKm, time);
              if (
                !fixed ||
                !Number.isFinite(fixed.x) ||
                !Number.isFinite(fixed.y) ||
                !Number.isFinite(fixed.z)
              ) {
                return undefined;
              }
              return fixed;
            } catch {
              return undefined;
            }
          },
          false, // not constant — must be re-evaluated every frame
          Cesium.ReferenceFrame.FIXED,
        );
        const entity = viewer.entities.add({
          id,
          name,
          position: posProperty,
          point: makePointGraphics(Cesium, Cesium.Color.CYAN),
          label: showLabels
            ? new Cesium.LabelGraphics(makeLabelOpts(Cesium, name))
            : undefined,
        });
        satMapRef.current.set(id, entity);
      }

      // ── Coverage ─────────────────────────────────────────────────────────
      const explicitCov = (satelliteCoverages ?? []).find(
        (c) => c.satelliteId === id,
      );
      if (explicitCov) {
        renderExplicitCoverage(
          viewer,
          Cesium,
          id,
          pos3,
          explicitCov,
          jd,
          covMapRef,
          coneMapRef,
          nadirMapRef,
        );
      } else if (showCoverage) {
        // Auto coverage: circular ellipse computed from sub-satellite point
        const subSat = eciKmToSubSatellite(Cesium, pos3, jd);
        if (subSat && subSat.altitude > 0) {
          const subSatPos = Cesium.Cartesian3.fromDegrees(
            subSat.longitude,
            subSat.latitude,
            0,
          );
          const radius = autoFootprintRadiusM(subSat.altitude, 5);
          upsertEllipse(
            viewer,
            Cesium,
            `cov-${id}`,
            subSatPos,
            radius,
            Cesium.Color.CYAN,
            covMapRef,
          );
        }
      } else {
        removeFromMap(viewer, `cov-${id}`, covMapRef);
        removeFromMap(viewer, `cone-${id}`, coneMapRef);
        removeFromMap(viewer, `nadir-${id}`, nadirMapRef);
      }
    });

    // Remove stale satellite entities (+ associated coverage geometry + ECI position).
    satMapRef.current.forEach((_, id) => {
      if (!seenSats.has(id)) {
        eciPositionsRef.current.delete(id);
        removeFromMap(viewer, id, satMapRef);
        removeFromMap(viewer, `cov-${id}`, covMapRef);
        removeFromMap(viewer, `cone-${id}`, coneMapRef);
        removeFromMap(viewer, `nadir-${id}`, nadirMapRef);
      }
    });

    // ── Custom objects (planets, celestial bodies, etc.) ────────────────
    // Earth is already rendered by Cesium's globe — skip it by name.
    // Other celestial bodies (Moon, etc.) are rendered as orange dots.
    // When `visibility.celestialBodies === false`, feed an empty list so
    // the cleanup pass removes any existing custom-object entities.
    const seenCustom = new Set<string>();
    const renderedCustom = showCelestialBodies ? (customObjects ?? []) : [];
    renderedCustom.forEach((obj) => {
      if (/^earth$/i.test(obj.name ?? "")) return; // skip Earth
      const id = obj.id;
      const cesPos = eciKmToFixedCartesian3(Cesium, obj.position, jd);
      if (!cesPos) return;
      seenCustom.add(id);

      const existing = satMapRef.current.get(`custom-${id}`);
      if (existing) {
        existing.position = new Cesium.ConstantPositionProperty(cesPos);
        if (existing.label) {
          existing.label.show = new Cesium.ConstantProperty(showLabels);
        } else if (showLabels) {
          existing.label = new Cesium.LabelGraphics(
            makeLabelOpts(Cesium, obj.name ?? id, Cesium.Color.ORANGE),
          );
        }
      } else {
        const entity = viewer.entities.add({
          id: `custom-${id}`,
          name: obj.name,
          position: cesPos,
          point: makePointGraphics(Cesium, Cesium.Color.ORANGE, 10),
          label: showLabels
            ? new Cesium.LabelGraphics(
                makeLabelOpts(Cesium, obj.name ?? id, Cesium.Color.ORANGE),
              )
            : undefined,
        });
        satMapRef.current.set(`custom-${id}`, entity);
      }
    });
    // Remove stale custom objects
    satMapRef.current.forEach((_, key) => {
      if (key.startsWith("custom-") && !seenCustom.has(key.slice(7))) {
        removeFromMap(viewer, key, satMapRef);
      }
    });

    // ── Ground stations ──────────────────────────────────────────────────
    // When `visibility.groundStations === false`, feed an empty list so
    // the cleanup pass removes any existing ground-station entities.
    const seenGs = new Set<string>();
    const renderedGs = showGroundStations ? (groundStations ?? []) : [];
    renderedGs.forEach((gs) => {
      if (!Number.isFinite(gs.longitude) || !Number.isFinite(gs.latitude))
        return;
      seenGs.add(gs.id);
      const heightM = Number.isFinite((gs as any).elevation)
        ? (gs as any).elevation
        : 0;
      const gsPos = Cesium.Cartesian3.fromDegrees(
        gs.longitude,
        gs.latitude,
        heightM,
      );
      const name = gs.name ?? gs.id;

      const existing = gsMapRef.current.get(gs.id);
      if (existing) {
        existing.position = new Cesium.ConstantPositionProperty(gsPos);
        if (existing.label) {
          existing.label.show = new Cesium.ConstantProperty(showLabels);
        } else if (showLabels) {
          existing.label = new Cesium.LabelGraphics(
            makeLabelOpts(Cesium, name, Cesium.Color.YELLOW),
          );
        }
      } else {
        const entity = viewer.entities.add({
          id: gs.id,
          name,
          position: gsPos,
          point: makePointGraphics(Cesium, Cesium.Color.YELLOW, 6),
          label: showLabels
            ? new Cesium.LabelGraphics(
                makeLabelOpts(Cesium, name, Cesium.Color.YELLOW),
              )
            : undefined,
        });
        gsMapRef.current.set(gs.id, entity);
      }
    });

    gsMapRef.current.forEach((_, id) => {
      if (!seenGs.has(id)) removeFromMap(viewer, id, gsMapRef);
    });

    // ── Orbit paths ──────────────────────────────────────────────────────
    if (showOrbits) {
      const seenOrbits = new Set<string>();

      (orbitPaths ?? []).forEach((path) => {
        const orbitId = `orbit-${path.objectId}`;
        seenOrbits.add(orbitId);
        const positions = path.points
          .map((p) => eciKmToFixedCartesian3(Cesium, p, jd))
          .filter((p): p is NonNullable<typeof p> => p != null);
        if (positions.length < 2) return;

        const orbitColor = path.color
          ? Cesium.Color.fromCssColorString(path.color).withAlpha(0.65)
          : Cesium.Color.YELLOW.withAlpha(0.55);

        const existing = orbitMapRef.current.get(orbitId);
        if (existing) {
          existing.polyline.positions = new Cesium.ConstantProperty(positions);
        } else {
          const entity = viewer.entities.add({
            id: orbitId,
            name: path.objectId,
            polyline: {
              positions,
              width: 1.5,
              material: new Cesium.PolylineDashMaterialProperty({
                color: orbitColor,
                dashLength: 16,
              }),
            },
          });
          orbitMapRef.current.set(orbitId, entity);
        }
      });

      orbitMapRef.current.forEach((_, id) => {
        if (!seenOrbits.has(id)) removeFromMap(viewer, id, orbitMapRef);
      });
    } else {
      // Orbits turned off: remove all
      orbitMapRef.current.forEach((_, id) =>
        removeFromMap(viewer, id, orbitMapRef),
      );
    }
  }, [
    Cesium,
    satellites,
    stations,
    customObjects,
    groundStations,
    orbitPaths,
    satelliteCoverages,
    showLabels,
    showCoverage,
    showOrbits,
    showSpacecraft,
    showGroundStations,
    showCelestialBodies,
    referenceTime,
  ]);

  // ── React to `view` prop changes ──────────────────────────────────────────
  // The imperative `setView()` handle is exposed for ad-hoc fly-tos. When
  // the consumer drives the camera declaratively via the `view` prop
  // instead, fly to the matching default destination on each change.
  useEffect(() => {
    setView(view);
    // setView itself depends on Cesium (constant once mounted), so it's
    // stable enough to omit from the dep array — but include `view` itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const h = typeof height === "number" ? `${height}px` : height;
  return (
    <div
      ref={containerRef}
      className="zendir-zenspace3d-cesium"
      style={{ width: "100%", height: h, minHeight: 200 }}
    />
  );
});

export default ZenSpace3DCesium;
